import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
  ChangePasswordPayload,
  EditProfilePayload,
  UserProfile,
  VerifyEmailChangePayload,
} from "./types";

interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: boolean;
}

export interface UserDetailState {
  profile: AsyncState<UserProfile>;
  profilePic: AsyncState<string>;
  changePassword: {
    saving: boolean;
    error: boolean;
    success: boolean;
  };
  editProfile: {
    saving: boolean;
    error: boolean;
    success: boolean;
  };
  emailVerification: {
    visible: boolean;
    pendingEmail: string;
    verifying: boolean;
    error: string | null;
  };
}

export const initialState: UserDetailState = {
  profile: { data: null, loading: false, error: false },
  profilePic: { data: null, loading: false, error: false },
  changePassword: { saving: false, error: false, success: false },
  editProfile: { saving: false, error: false, success: false },
  emailVerification: {
    visible: false,
    pendingEmail: "",
    verifying: false,
    error: null,
  },
};

export const userDetailSlice = createSlice({
  name: "userDetail",
  initialState,
  reducers: {
    loadProfile(state) {
      state.profile.loading = true;
      state.profile.error = false;
    },
    profileLoaded(state, { payload }: PayloadAction<UserProfile>) {
      state.profile.data = payload;
      state.profile.loading = false;
      state.profile.error = false;
    },
    loadProfileError(state) {
      state.profile.loading = false;
      state.profile.error = true;
    },
    loadProfilePicture(state) {
      state.profilePic.loading = true;
      state.profilePic.error = false;
    },
    profilePictureLoaded(state, { payload }: PayloadAction<string>) {
      state.profilePic.data = payload;
      state.profilePic.loading = false;
      state.profilePic.error = false;
    },
    loadProfilePictureError(state) {
      state.profilePic.loading = false;
      state.profilePic.error = true;
    },
    profilePicUpdated(state, { payload }: PayloadAction<string | null>) {
      state.profilePic.data = payload;
    },

    savePassword(state, _action: PayloadAction<ChangePasswordPayload>) {
      state.changePassword.saving = true;
      state.changePassword.error = false;
      state.changePassword.success = false;
    },
    passwordSaved(state) {
      state.changePassword.saving = false;
      state.changePassword.error = false;
      state.changePassword.success = true;
    },
    savePasswordError(state) {
      state.changePassword.saving = false;
      state.changePassword.error = true;
      state.changePassword.success = false;
    },
    changePasswordReset(state) {
      state.changePassword = { saving: false, error: false, success: false };
    },

    saveProfile(state, _action: PayloadAction<EditProfilePayload>) {
      state.editProfile.saving = true;
      state.editProfile.error = false;
      state.editProfile.success = false;
    },
    profileSaved(state, { payload }: PayloadAction<{ full_name: string }>) {
      state.editProfile.saving = false;
      state.editProfile.error = false;
      state.editProfile.success = true;
      if (state.profile.data) {
        state.profile.data.full_name = payload.full_name;
      }
    },
    saveProfileError(state) {
      state.editProfile.saving = false;
      state.editProfile.error = true;
      state.editProfile.success = false;
    },
    editProfileReset(state) {
      state.editProfile = { saving: false, error: false, success: false };
    },

    showEmailVerifyModal(state, { payload }: PayloadAction<string>) {
      state.emailVerification.visible = true;
      state.emailVerification.pendingEmail = payload;
      state.emailVerification.error = null;
    },
    hideEmailVerifyModal(state) {
      state.emailVerification.visible = false;
      state.emailVerification.pendingEmail = "";
      state.emailVerification.error = null;
    },
    verifyEmailChange(
      state,
      _action: PayloadAction<VerifyEmailChangePayload>,
    ) {
      state.emailVerification.verifying = true;
      state.emailVerification.error = null;
    },
    emailChangeVerified(state) {
      state.emailVerification.verifying = false;
      state.emailVerification.visible = false;
      state.emailVerification.error = null;
    },
    verifyEmailChangeError(state, { payload }: PayloadAction<string>) {
      state.emailVerification.verifying = false;
      state.emailVerification.error = payload;
    },
  },
});

export const {
  loadProfile,
  profileLoaded,
  loadProfileError,
  loadProfilePicture,
  profilePictureLoaded,
  loadProfilePictureError,
  profilePicUpdated,
  savePassword,
  passwordSaved,
  savePasswordError,
  changePasswordReset,
  saveProfile,
  profileSaved,
  saveProfileError,
  editProfileReset,
  showEmailVerifyModal,
  hideEmailVerifyModal,
  verifyEmailChange,
  emailChangeVerified,
  verifyEmailChangeError,
} = userDetailSlice.actions;

export const { reducer } = userDetailSlice;
